import React, { useState } from "react";
import { motion } from "framer-motion";
import { 
  Clock, 
  Users, 
  Star, 
  CheckCircle, 
  ArrowRight, 
  Zap, 
  Heart,
  Shield,
  Calendar,
  MessageCircle
} from "lucide-react";

const ServiceCard = ({ service }) => {
  const [isLiked, setIsLiked] = useState(false);

  const handleWhatsAppClick = () => {
    const message = `¡Hola! Estoy interesado en el servicio: *${service.name}*

💵 *Precio:* $${service.price}${service.originalPrice ? ` (Antes: $${service.originalPrice})` : ''}
⭐ *Rating:* ${service.rating}/5
⏱️ *Duración:* ${service.duration} horas
📦 *Entrega:* ${service.deliveryTime}

📋 *Descripción:*
${service.description}

✨ *Características principales:*
${service.features.slice(0, 3).map(feature => `• ${feature}`).join('\n')}

Me gustaría obtener más información sobre este servicio. ¡Gracias!`;

    const encodedMessage = encodeURIComponent(message);
    const phoneNumber = "51955000321";
    const whatsappUrl = `https://wa.me/${phoneNumber}?text=${encodedMessage}`;
    
    window.open(whatsappUrl, '_blank');
  };

  // Calcular características a mostrar (máximo 3)
  const visibleFeatures = service.features.slice(0, 3);
  const hiddenFeaturesCount = service.features.length - 3;

  return (
    <motion.div
      whileHover={{ y: -8, scale: 1.02 }}
      whileTap={{ scale: 0.98 }}
      className="group relative bg-gradient-to-br from-white to-gray-50/80 rounded-3xl shadow-xl hover:shadow-2xl transition-all duration-500 overflow-hidden border border-white/20 backdrop-blur-sm flex flex-col h-[650px]" // Altura fija optimizada
    >
      {/* Efecto de brillo en hover */}
      <div className="absolute inset-0 bg-gradient-to-br from-teal-500/5 via-transparent to-blue-500/5 opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
      
      {/* Badge de popular con efecto neón */}
      {service.isPopular && (
        <motion.div
          initial={{ scale: 0, rotate: -45 }}
          animate={{ scale: 1, rotate: 0 }}
          className="absolute -top-3 -right-3 z-20"
        >
          <div className="relative">
            <span className="px-4 py-2 bg-gradient-to-r from-amber-400 to-orange-500 text-white text-xs font-black rounded-full shadow-lg shadow-orange-500/30 flex items-center gap-1">
              <Zap className="w-3 h-3 fill-current" />
              POPULAR
            </span>
            <div className="absolute inset-0 bg-gradient-to-r from-amber-400 to-orange-500 rounded-full blur-sm opacity-50 -z-10" />
          </div>
        </motion.div>
      )}

      {/* Header con imagen - Altura fija */}
      <div className="relative h-48 overflow-hidden flex-shrink-0">
        <motion.img
          src={service.image}
          alt={service.name}
          className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
          whileHover={{ scale: 1.1 }}
        />
        
        {/* Overlay gradient mejorado */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent" />
        
        {/* Botón de like */}
        <motion.button
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.9 }}
          onClick={() => setIsLiked(!isLiked)}
          className="absolute top-4 right-4 w-8 h-8 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center hover:bg-white/30 transition-colors z-10"
        >
          <Heart 
            className={`w-4 h-4 transition-all duration-300 ${
              isLiked 
                ? "fill-rose-500 text-rose-500 scale-110" 
                : "text-white"
            }`} 
          />
        </motion.button>

        {/* Categoría con glassmorphism */}
        <div className="absolute top-4 left-4">
          <span className="px-4 py-2 bg-white/20 backdrop-blur-md rounded-2xl text-sm font-semibold text-white border border-white/30 shadow-lg">
            {service.category}
          </span>
        </div>

        {/* Rating con efecto glow */}
        <div className="absolute bottom-4 left-4 flex items-center gap-2 px-3 py-2 bg-black/40 backdrop-blur-md rounded-2xl border border-white/20">
          <Star className="w-4 h-4 text-yellow-400 fill-current" />
          <span className="text-white font-bold text-sm">{service.rating}</span>
          <span className="text-white/80 text-xs">({service.clients})</span>
        </div>
      </div>

      {/* Content - Distribución fija sin scroll */}
      <div className="relative p-6 flex flex-col flex-1">
        
        {/* Título y precio - Altura fija */}
        <div className="flex justify-between items-start mb-3 flex-shrink-0">
          <h3 className="text-xl font-bold text-gray-900 group-hover:text-transparent group-hover:bg-gradient-to-r group-hover:from-teal-600 group-hover:to-blue-600 group-hover:bg-clip-text transition-all duration-500 flex-1 pr-4 line-clamp-2 min-h-[3rem]">
            {service.name}
          </h3>
          <div className="text-right flex-shrink-0">
            <div className="flex items-baseline gap-1">
              {service.originalPrice && (
                <span className="text-sm text-gray-400 line-through">
                  ${service.originalPrice}
                </span>
              )}
              <span className="text-2xl font-black bg-gradient-to-r from-teal-600 to-blue-600 bg-clip-text text-transparent">
                ${service.price}
              </span>
            </div>
            <div className="text-xs text-gray-500 mt-1">Precio final</div>
          </div>
        </div>

        {/* Descripción - Altura fija */}
        <div className="mb-4 flex-shrink-0">
          <p className="text-gray-600 text-sm leading-relaxed line-clamp-2 min-h-[2.5rem]">
            {service.description}
          </p>
        </div>

        {/* Características - Altura fija sin scroll */}
        <div className="mb-4 flex-1">
          <div className="space-y-2">
            {visibleFeatures.map((feature, index) => (
              <motion.div 
                key={index}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: index * 0.1 }}
                className="flex items-start gap-3 group/feature"
              >
                <div className="relative flex-shrink-0 mt-0.5">
                  <CheckCircle className="w-4 h-4 text-teal-500 group-hover/feature:scale-110 transition-transform duration-300" />
                </div>
                <span className="text-sm text-gray-700 font-medium leading-tight flex-1">
                  {feature}
                </span>
              </motion.div>
            ))}
          </div>
          
          {/* Indicador de características adicionales */}
          {hiddenFeaturesCount > 0 && (
            <div className="mt-2 text-xs text-teal-600 font-medium bg-teal-50 rounded-lg px-3 py-1 inline-block">
              +{hiddenFeaturesCount} características más
            </div>
          )}
        </div>

        {/* Meta información - Altura fija */}
        <div className="flex items-center justify-between mb-4 p-3 bg-gradient-to-r from-gray-50 to-teal-50/30 rounded-2xl border border-gray-100 flex-shrink-0">
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2 text-gray-600">
              <div className="p-1.5 bg-teal-100 rounded-lg">
                <Clock className="w-3.5 h-3.5 text-teal-600" />
              </div>
              <div>
                <div className="text-xs font-semibold">{service.duration}h</div>
                <div className="text-[10px] text-gray-500">Duración</div>
              </div>
            </div>
            
            <div className="flex items-center gap-2 text-gray-600">
              <div className="p-1.5 bg-blue-100 rounded-lg">
                <Users className="w-3.5 h-3.5 text-blue-600" />
              </div>
              <div>
                <div className="text-xs font-semibold">{service.clients}+</div>
                <div className="text-[10px] text-gray-500">Clientes</div>
              </div>
            </div>
          </div>
          
          {service.deliveryTime && (
            <div className="text-right">
              <div className="flex items-center gap-1 text-teal-600 font-semibold text-xs">
                <Calendar className="w-3.5 h-3.5" />
                {service.deliveryTime}
              </div>
              <div className="text-[10px] text-gray-500">Entrega</div>
            </div>
          )}
        </div>

        {/* Botón de WhatsApp - Altura fija */}
        <div className="flex-shrink-0">
          <motion.button
            onClick={handleWhatsAppClick}
            whileHover={{ 
              scale: 1.02, 
              boxShadow: "0 20px 40px rgba(20, 184, 166, 0.25)" 
            }}
            whileTap={{ scale: 0.98 }}
            className="w-full bg-gradient-to-r from-teal-500 to-emerald-500 hover:from-teal-600 hover:to-emerald-600 text-white py-3.5 px-4 rounded-2xl font-bold text-base flex items-center justify-center gap-3 transition-all duration-300 group/btn relative overflow-hidden border border-teal-400/20"
          >
            {/* Efecto de brillo sutil */}
            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent -skew-x-12 translate-x-[-100%] group-hover/btn:translate-x-[100%] transition-transform duration-1000" />
            
            <MessageCircle className="w-5 h-5 relative z-10" />
            <span className="relative z-10">Contactar por WhatsApp</span>
          </motion.button>

          {/* Información de contacto rápida */}
          <div className="flex items-center justify-center gap-2 mt-2 text-gray-500 text-xs">
            <Shield className="w-3 h-3 text-teal-500" />
            <span>Respuesta rápida garantizada</span>
          </div>
        </div>
      </div>

      {/* Efecto de borde animado */}
      <div className="absolute inset-0 rounded-3xl bg-gradient-to-r from-teal-500/0 via-teal-500/10 to-blue-500/0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 -z-10" />
    </motion.div>
  );
};

export default ServiceCard;